'use client'

import React, { createContext, useContext, useState, useCallback } from 'react'
import { DEFAULT_DISTANCE } from '@/src/data'
import { CurrentAddressData } from '@/src/interfaces'

import { GeoLocation, Query, QueryStringParams } from '@/src/interfaces'

interface SearchContextType {
  query: Query
  setQuery: React.Dispatch<React.SetStateAction<Query>>
  searchParams: QueryStringParams
  setSearchParams: React.Dispatch<React.SetStateAction<QueryStringParams>>
  currentSearchBarCategory: string
  setCurrentSearchBarCategory: (category: string) => void
  shouldClearSearch: boolean
  setShouldClearSearch: React.Dispatch<React.SetStateAction<boolean>>
  locationError: string | null
  setLocationError: React.Dispatch<React.SetStateAction<string | null>>
  addressData: CurrentAddressData | null
  setAddressData: (data: CurrentAddressData | null) => void
  geoLocation: GeoLocation | null
  setGeoLocation: (location: GeoLocation | null) => void
  clearAllLocationData: () => void
}

const SearchContext = createContext<SearchContextType | undefined>(undefined)

export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [query, setQuery] = useState<Query>({
    singleValueParameters: { key: '', value: '' },
    locationParameters: { state: '', city: '', zip: '' },
    insuranceParameters: '',
    checkBoxedParameters: {
      booking_url: false,
      telehealth_appointment: false,
      accepts_new_patients: false,
    },
    geoDistanceParameters: {
      distance: DEFAULT_DISTANCE,
      currentLocation: { lat: 0, lon: 0 },
    },
    genderParameters: '',
    languageParameters: '',
  })

  const [searchParams, setSearchParams] = useState<QueryStringParams>({
    limit: 10,
    offset: 0,
    sortBy: 'last_name',
    sortOrder: 'asc',
  })

  const [locationError, setLocationError] = useState<string | null>(null)

  const [currentSearchBarCategory, setCurrentSearchBarCategory] =
    useState('unified')

  const [shouldClearSearch, setShouldClearSearch] = useState(false)

  const [addressData, setAddressData] = useState<CurrentAddressData | null>(
    null
  )
  const [geoLocation, setGeoLocation] = useState<GeoLocation | null>(null)

  const clearAllLocationData = useCallback(() => {
    setAddressData(null)
    setGeoLocation(null)
    setLocationError(null)
    setQuery((prev) => ({
      ...prev,
      locationParameters: { state: '', city: '', zip: '' },
      geoDistanceParameters: {
        distance: DEFAULT_DISTANCE,
        currentLocation: { lat: 0, lon: 0 },
      },
    }))
  }, [])

  return (
    <SearchContext.Provider
      value={{
        query,
        setQuery,
        searchParams,
        setSearchParams,
        currentSearchBarCategory,
        setCurrentSearchBarCategory,
        shouldClearSearch,
        setShouldClearSearch,
        locationError,
        setLocationError,
        addressData,
        setAddressData,
        geoLocation,
        setGeoLocation,
        clearAllLocationData,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export function useSearch() {
  const context = useContext(SearchContext)
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider')
  }
  return context
}
